import { useToast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { PAGE_SIZE } from "@shared/utils/constants";
import { FileText, Info, Mail, MessageSquare, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { useConsumerList, useDownloadBill, useViewBill } from "../../hooks";
import { ActionButton } from "./ActionButton";

// Channel display configuration with icons
const channelConfig = {
  Email: { icon: Mail, label: "Email", color: "text-blue-500" },
  SMS: { icon: MessageSquare, label: "SMS", color: "text-green-500" },
  Paper: { icon: FileText, label: "Paper Bill", color: "text-amber-500" },
  WhatsApp: { icon: Phone, label: "WhatsApp", color: "text-emerald-500" },
};

interface ConsumerData {
  id: number;
  billNumber: string;
  billCycleName: string;
  billAmount: number;
  consumerName: string;
  isEmailChannel: boolean;
  isPaperBillChannel: boolean;
  isEmailSent: boolean;
  isPaperBillSent: boolean;
  isEmailDelivered: boolean | null;
  isPaperBillDelivered: boolean | null;
  isEmailFailed: boolean;
}

interface ConsumerTableProps {
  activeFilter: string | null;
}

export function ConsumerTable({ activeFilter }: ConsumerTableProps) {
  const params = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [appliedFilter, setAppliedFilter] = useState<string>("");
  const { toast } = useToast();
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const { data, refetch } = useConsumerList({
    bill_run_id: params?.id,
    page: currentPage,
    pageSize: PAGE_SIZE,
    search_data: searchTerm.length > 2 ? searchTerm : "",
    filter: selectedFilter,
    sort_field: sortField,
  });

  const currency = useCurrency();

  // Helper function to get channels based on data
  const getChannels = (
    consumer: ConsumerData,
    type: "opted" | "sent" | "delivered" | "failed"
  ) => {
    const channels: string[] = [];

    switch (type) {
      case "opted":
        if (consumer.isEmailChannel) channels.push("Email");
        if (consumer.isPaperBillChannel) channels.push("Paper");
        break;
      case "sent":
        if (consumer.isEmailSent) channels.push("Email");
        if (consumer.isPaperBillSent) channels.push("Paper");
        break;
      case "delivered":
        if (consumer.isEmailDelivered === true) channels.push("Email");
        if (consumer.isPaperBillDelivered === true) channels.push("Paper");
        break;
      case "failed":
        if (consumer.isEmailChannel) channels.push("Email");
        // Add paper bill failed logic if available in API
        break;
    }

    return channels;
  };

  // Helper function to render channel icons with tooltips
  const renderChannelIcons = (channels: string[], className: string = "") => {
    if (channels.length === 0) return "-";

    return (
      <div className="flex flex-wrap gap-1">
        {channels.map((channel) => {
          const config = channelConfig[channel as keyof typeof channelConfig];
          if (!config) return null;

          const IconComponent = config.icon;

          return (
            <TooltipProvider key={channel}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`rounded-md p-1 ${className} hover:bg-muted cursor-help`}
                  >
                    <IconComponent className={`h-4 w-4 ${config.color}`} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{config.label}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  };

  // Define columns for GenericTable
  const columns = [
    {
      key: "consumerName",
      header: "Consumer Name",
      sortable: true,
      render: (value: string, row: ConsumerData) => {
        const missingContactInfo =
          !row.isEmailChannel && !row.isPaperBillChannel;
        return (
          <div className="font-medium flex items-center gap-1">
            {value}
            {missingContactInfo && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Missing contact information</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      key: "consumerNumber",
      header: "Consumer No",
    },
    {
      key: "billNumber",
      sortable: true,
      header: "Bill Number",
    },
    {
      key: "billAmount",
      sortable: true,
      header: "Bill Amount",
      render: (value: number) => (
        <div className="font-medium">
          {currency} {value}
        </div>
      ),
    },
    {
      key: "optedChannels",
      header: "Opted Channels",
      render: (value: any, row: ConsumerData) => {
        const channels: string[] = [];
        if (row.isEmailChannel) channels.push("Email");
        if (row.isPaperBillChannel) channels.push("Paper");
        return renderChannelIcons(channels);
      },
    },
    {
      key: "sentChannels",
      header: "Sent",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "sent");
        return renderChannelIcons(channels);
      },
    },
    {
      key: "deliveredChannels",
      header: "Delivered",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "delivered");
        return renderChannelIcons(channels, "bg-green-100");
      },
    },
    {
      key: "readChannels",
      header: "Read",
      render: (value: any, row: ConsumerData) => {
        // Add read logic when available in API
        return renderChannelIcons([], "bg-blue-100");
      },
    },
    {
      key: "failedChannels",
      header: "Failed",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "failed");
        return renderChannelIcons(channels, "bg-red-100");
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (value: any, row: ConsumerData) => {
        const failedChannels = getChannels(row, "failed");
        return (
          <div className="flex flex-row gap-1">
            <ActionButton
              type="view"
              onClick={() => handleViewClick(row.id)}
              iconOnly
            />
            <ActionButton
              type="download"
              onClick={() => handleDownloadClick(row.id)}
              iconOnly
            />
          </div>
        );
      },
    },
  ];

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();

  const handleDownloadClick = async (billId: number, filename?: string) => {
    const id = billId.toString();
    try {
      await downloadBill.mutateAsync({
        billId: id,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleViewClick = async (billId: number) => {
    try {
      await viewBill.mutateAsync(billId.toString());
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const handleRetryFailed = (consumerId: string, failedChannels: string[]) => {
    if (failedChannels.length === 0) return;
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilter = (filter: string) => {
    setAppliedFilter(filter);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortDirection(direction);
    setCurrentPage(1);
    switch (field) {
      case "billNumber":
        setSortField("bill_number");

        break;
      case "billAmount":
        setSortField("bill_amount");
        break;
      case "consumerName":
        setSortField("consumer_name");
        break;

      default:
        setSortField(field);
        break;
    }
  };

  const handleExport = () => {
    // Trigger export with current filters
    toast({
      title: "Export initiated",
      description: "Consumer data is being prepared for download",
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset page when activeFilter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  const filterOptions = [
    { value: "fullyDelivered", label: "Fully Delivered" },
    { value: "partiallyDelivered", label: "Partially Delivered" },
    { value: "notDelivered", label: "Not Delivered" },
    { value: "failed", label: "Failed Delivery" },
    { value: "missingContact", label: "Missing Contact Info" },
    { value: "emailChannel", label: "Email Channel Only" },
    { value: "paperChannel", label: "Paper Bill Only" },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Consumer Delivery Status</CardTitle>
          <CardDescription>
            Track bill delivery status across all communication channels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mt-5"></div>
          <GenericTable
            data={data?.result ?? []}
            columns={columns}
            rowKey="id"
            emptyMessage="No consumers found"
            pagination={{
              currentPage: currentPage,
              totalPages: Math.ceil((data?.count || 0) / PAGE_SIZE),
              totalItems: data?.count || 0,
              itemsPerPage: PAGE_SIZE,
              startItem: ((currentPage || 1) - 1) * PAGE_SIZE + 1,
              endItem: Math.min(
                (currentPage || 1) * PAGE_SIZE,
                data?.count || 0
              ),
            }}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            onSort={handleSort}
            onExport={handleExport}
            searchPlaceholder="Search by consumer name, ID, or bill number..."
          />
        </CardContent>
      </Card>
    </div>
  );
}
